import type { PendingReconcilePartitionedSnapshot, PendingReconcileSnapshot } from './internal-rating-pending.ts';
/**
 * Storage-autonomy P3 (#1410): the rating cron wire contracts.
 *
 * Two platform routes (`POST /internal/cron/rate-events` and its `/sweep`
 * sibling) and one gateway route (`POST /internal/usage-events/cleanup`) share
 * this module. The control-loops handlers that drive them read these types, so
 * every limit here is the SHARED cap rather than a local literal on either
 * end — see `RATE_EVENTS_MAX_LIMIT` for what a mismatch costs.
 */

/**
 * One drain or sweep page's counters.
 *
 * `queue_depth` is `null` when Tair is unreachable, and that SUPPRESSES its
 * alarm — a Tair blip is not a billing incident. `oldest_unrated_age_ms` is
 * `null` when the PG anti-join failed, and that FIRES its alarm: it is
 * computed from the exact component whose failure the alarm exists to report,
 * so "cannot tell" and "very bad" are the same operational state. The two
 * rules are deliberately opposite; applying either one uniformly reintroduces
 * the failure the other prevents.
 */
export interface RateEventsCronResponse {
  readonly ok: true;
  /** Events peeked off the queue (drain) or matched by the anti-join (sweep). */
  readonly claimed: number;
  readonly rated: number;
  readonly charged: number;
  readonly insufficient: number;
  readonly unpriced: number;
  /**
   * Events whose rated row already existed. MUST stay a distinct counter: a
   * persistently rising value is the signature of double-peeking drains or a
   * clamp mismatch re-fetching one page forever, and folding it into `rated`
   * erases the only evidence.
   */
  readonly skippedAlreadyRated: number;
  readonly failed: number;
  /**
   * Events left unrated because another rater already holds this account's
   * `account_balances` row (`FOR UPDATE SKIP LOCKED` returned nothing), or
   * because `lock_timeout` fired on a later statement of that transaction
   * (#2335). Not a failure: nothing was written, the ids stay
   * queued (drain) or are re-derived next tick (sweep). Absent or 0 means
   * this round did not skip for a lock. Optional so older clients and
   * exact-shape fixtures that omit it keep parsing.
   */
  readonly skipped_locked?: number;
  /**
   * Charges this round rated and committed whose post-commit credit-lot
   * attribution was deferred to `credit_draw_sweep` because the allocation
   * fallback breaker tripped (a run of allocation failures). Money is
   * unaffected; only which lot paid is late. Absent when 0.
   */
  readonly allocation_deferred?: number;
  /**
   * #2695 FL-5: events left unrated because `rate` enforces its freeze line and
   * this call lay behind it — nothing written, the events stay pending, and
   * they are not `failed`. Absent when 0.
   */
  readonly ledger_write_frozen?: number;
  /** Tair LLEN; null when Tair is unreachable. */
  readonly queue_depth: number | null;
  /**
   * Age of the oldest unrated event. null means EITHER the anti-join failed
   * OR (drain only, `stopped_early: true` present) this round skipped the
   * measurement to stay inside its budget. The two are told apart by
   * `stopped_early`; a scan that treats null as "cannot see" must check it.
   */
  readonly oldest_unrated_age_ms: number | null;
  /**
   * `billing_anomalies` rows with reason `usage_log_write_failed` inside the
   * metering-failure alarm's lookback window; null means the count query
   * failed, which SUPPRESSES that alarm (the anomaly rows it counts are
   * written through the same PG whose read just failed, and
   * `oldest_unrated_age_ms` already fires on a PG read failing). This is the
   * fourth alarm's observable (#1599): a failing metering transaction writes
   * no `usage_events` and no `usage_logs` row, so the anomaly row is the only
   * artifact a scan can see.
   */
  readonly usage_log_failed_recent: number | null;
  /**
   * Present only when the DRAIN stopped at `RATE_EVENTS_DRAIN_DEADLINE_MS`
   * before rating every fresh event on the page. The page was NOT acked: its
   * ids stay queued and the next round resumes them (finished ones are
   * filtered out when the page is loaded, so `skippedAlreadyRated` is not
   * inflated; the money-side idempotency is
   * `balance_transactions.external_request_id` + the rated-row PK, not that
   * filter). Never set by the sweep — it reports the same
   * condition as `status: 'budget_exhausted'`. Optional so existing
   * exact-shape assertions keep matching.
   */
  readonly stopped_early?: true;
  /**
   * Present only on the page-engine route (`POST /internal/cron/rate-events/page`,
   * #2684 L-B) and on a sweep asked for the page engine (`{ engine: 'page' }`,
   * sent by control-loops when `RATING_DRAIN_CONSUMERS` >= 2). The legacy
   * route and a K = 1 sweep never carry it, so a K = 1 body is byte-for-byte
   * today's.
   */
  readonly engine?: 'page';
}

/**
 * How one backstop sweep pass ended. Three outcomes, deliberately
 * distinguished — an operator reading `ok: true` alone cannot tell a pass
 * that rated its page from a pass that never ran, or from one that stopped
 * under its wall-clock budget with work still on the table.
 *
 * - `completed` — the pass finished the page it loaded (including the empty
 *   page). Counters are the whole story.
 * - `skipped` — a previous pass still held the sweep advisory lock. Not an
 *   error and not a failure to alert on: the running pass is doing the work.
 *   Overlapping sweeps on one account serialize on `account_balances` and
 *   collapse throughput; skipping is how the next tick stays out of that
 *   pile-up.
 * - `budget_exhausted` — the pass hit its internal deadline, committed every
 *   event it had already rated, and left the rest unrated for the next tick.
 *   Partial work is real work: reporting this as `ok: false` with claimed=0
 *   is the lie that turns a live handler into a zombie the client has
 *   abandoned.
 */
export type RateEventsSweepStatus = 'completed' | 'skipped' | 'budget_exhausted';

/**
 * The sweep's response additionally reports the block-recovery phase (task
 * D19), which runs on every tick regardless of whether the rating page was
 * empty — a blocked key produces no traffic by definition, so a recovery
 * reachable only from rated spend is a deadlock with the customer on the
 * wrong side of it.
 *
 * `status` is why `ok: true` is not enough. A skipped pass and a completed
 * empty pass are both `ok: true` with zero counters; only `status` tells
 * the tick log which one happened.
 */
export interface RateEventsSweepCronResponse extends RateEventsCronResponse {
  readonly status: RateEventsSweepStatus;
  /** `rating_key_block_state` rows with `threshold_blocked = TRUE` examined. */
  readonly recovery_examined: number;
  /** Keys re-evaluated to below-threshold and un-blocked this pass. */
  readonly recovery_recovered: number;
  /** Still over a live ceiling; left blocked. */
  readonly recovery_still_blocked: number;
  /**
   * #2699: the pending-set reconciliation after this pass's run, present only
   * while `RATING_UNRATED_SOURCE=pending_table` (the anti_join body is
   * unchanged). #2750: version 5 under `RATING_PENDING_RECONCILE=partitioned`.
   */
  readonly pending_reconcile?: PendingReconcileSnapshot | PendingReconcilePartitionedSnapshot;
}

/**
 * Rows per drain page, and the ONE authority for that number. All three
 * enforcement points must agree — the peek script's bound, the platform
 * route's clamp, and the control-loops `PAGE_LIMIT`. A mismatch does not
 * error: asking for more than a server cap gets silently clamped, a clamped
 * page always looks "short", and a short page is exactly what the drain loop
 * reads as "queue drained". That is how #1501's ¥24,802 backlog accumulated
 * for 20 hours while every tick reported success.
 */
export const RATE_EVENTS_MAX_LIMIT = 500;

/**
 * Rows per backstop sweep page. Distinct from `RATE_EVENTS_MAX_LIMIT`
 * because the drain loops and the sweep does not: one sweep tick must
 * finish inside `RATE_EVENTS_SWEEP_DEADLINE_MS`, and a page the deadline
 * cannot rate simply ends as `budget_exhausted` (rated-so-far committed,
 * the rest is the next tick's job).
 *
 * 400 (#2288, was 80). Sized on production 2026-09-04, a single-tenant
 * burst — the worst case, because same-account events rate serially on
 * the account row lock: 118 sweep runs at 80 rows measured p50 0.0875 s /
 * p90 0.1125 s per event, max 0.30 s under manual-sweep contention. So
 * 400 × 0.1125 s ≈ 45 s, half the 90 s deadline; the contended 0.30 s/event
 * would end the page at the deadline as `budget_exhausted`, which is the
 * designed outcome, not a failure. At the unchanged 10-minute cadence this
 * is 2 400 rows/h against the 480/h (80 × 6) that let ~6 000 rows sit
 * unrated for hours that day. The cadence itself is deliberately NOT
 * shortened: any 5-minute interval lands on a residue mod 5, and residue 3
 * is the reserved tick no 5-minute handler may occupy (three suites pin
 * it). The 10/6 slot shares every tick with api_key_last_used_fold (5/1,
 * order 63) — fine, the sweep is order 65 and in fact the highest-order
 * handler on every tick it shares, which is what licenses its 145 s hold.
 * The old "0.5–1 s per event" figure predates the current rating path.
 *
 * 2000 (2026-09-07, was 400). #2332/#2335 settle a page per account batch,
 * so the per-event serial cost above no longer applies: production measured
 * 100–130 events/s per rater (a 522-row drain round in 4 s, 1614 in 15.6 s),
 * so 2000 rows ≈ 15–20 s, still a quarter of the 90 s deadline; a slow page
 * still ends as `budget_exhausted`. Sized against the fast lane's loss rate:
 * a 1000 RPM CN burst dropped 18% of its ids from the Tair queue (the CN→Tokyo
 * producer is fire-and-forget by design), i.e. ~180 rows/min falling to the
 * sweep against the 40 rows/min that 400 × 6 could absorb. 12 000 rows/h
 * covers that up to ~5000 RPM, which is where the fast lane itself tops out.
 * Cadence unchanged for the residue-3 reason above.
 */
export const RATE_EVENTS_SWEEP_MAX_LIMIT = 2000;

/**
 * Same-account events settled in ONE PostgreSQL transaction by the rating
 * engine (#2332). Distinct from `RATE_EVENTS_MAX_LIMIT` / the sweep page:
 * those bound how many events a drain or sweep *claims*; this bounds how
 * many of one account's claimed events share a single `account_balances
 * FOR UPDATE`. A page for one account larger than this is split into
 * consecutive batches, each its own transaction. Deadline checks run
 * between batches, never inside one — a batch that has started completes.
 *
 * 200. Default / sweep size. Drain uses the smaller
 * {@link RATE_EVENTS_DRAIN_ACCOUNT_BATCH_MAX}: a 200-row batch holding
 * `account_balances FOR UPDATE` for seconds is what made two platform
 * hosts + sweep all time out on the same account (#2335). Sweep's 90 s
 * deadline can still afford 200. Raising toward 400 is a local-rehearsal
 * decision; the sweep page (`RATE_EVENTS_SWEEP_MAX_LIMIT`) is a separate
 * Issue.
 */
export const RATE_EVENTS_ACCOUNT_BATCH_MAX = 200;

/**
 * Drain-only same-account batch cap (#2335). Distinct from
 * {@link RATE_EVENTS_ACCOUNT_BATCH_MAX} (sweep / default 200) because a
 * cross-region platform host (Los Angeles → Tokyo RDS) pays 100 ms+ per
 * statement inside the account lock; 200 rows held the row for seconds
 * and every concurrent rater hit `lock_timeout`, counting the whole
 * page failed. 100 keeps lock-hold time inside the drain's 1 s
 * `lock_timeout` budget on that path while a 500-row one-account page
 * is still five batches, well inside `RATE_EVENTS_DRAIN_DEADLINE_MS`.
 */
export const RATE_EVENTS_DRAIN_ACCOUNT_BATCH_MAX = 100;

/**
 * `SET LOCAL lock_timeout` for the drain rating path's account
 * `FOR UPDATE` (#2332 / #2283 / #2335). Drain's wall clock is
 * `RATE_EVENTS_DRAIN_DEADLINE_MS` (5 s); waiting longer than 1 s for a
 * contended account lock (the drain-vs-sweep pile-up of #2280) would
 * consume the remainder of a drain round without writing. Timeout is
 * classified as `skipped_locked` (no writes, no ack); the next round or
 * the sweep retries. Sweep uses the longer
 * {@link RATE_EVENTS_SWEEP_LOCK_TIMEOUT_MS}.
 */
export const RATE_EVENTS_DRAIN_LOCK_TIMEOUT_MS = 1_000;

/**
 * `SET LOCAL lock_timeout` for the backstop sweep's account `FOR UPDATE`.
 * Sweep's wall clock is `RATE_EVENTS_SWEEP_DEADLINE_MS` (90 s). 5 s lets
 * a sweep batch wait out a drain that holds the same account lock for a
 * drain-sized write without burning the rest of the 90 s tick. Timeout
 * is `skipped_locked`, not failed: the next sweep tick re-derives the
 * page.
 */
export const RATE_EVENTS_SWEEP_LOCK_TIMEOUT_MS = 5_000;

/**
 * Internal wall-clock budget for one platform sweep pass.
 *
 * OWNED BY CONTRACTS BECAUSE IT IS A TWO-PARTY BOUND. The platform rates
 * (and, if the budget hits, stops) under this deadline and then answers
 * `completed` / `skipped` / `budget_exhausted`; the control-loops client
 * must outlive it to hear that answer. A client bound below this number is
 * how a live handler becomes a zombie: the worker logs `ok: false` while
 * the platform keeps rating, and the next tick overlaps the same page.
 */
export const RATE_EVENTS_SWEEP_DEADLINE_MS = 90_000;

/**
 * Optional sweep request field (batch 2 review R6): control-loops sends the
 * pass budget its tick has left, and the platform stops starting work that
 * much after the request ARRIVED (pre-lock guard reads included) instead of
 * RATE_EVENTS_SWEEP_DEADLINE_MS after the pass began. Never above that
 * deadline; absent (a manual run, an older control-loops) = today's pass.
 */
export const RATE_EVENTS_SWEEP_PASS_BUDGET_FIELD = 'passBudgetMs';

/**
 * Internal wall-clock budget for ONE drain page (`POST /internal/cron/rate-events`).
 *
 * OWNED BY CONTRACTS BECAUSE IT IS A TWO-PARTY BOUND, same as the sweep's:
 * the platform stops STARTING new events at this instant and answers with
 * `stopped_early: true` and the page un-acked; the control-loops client's
 * `ROUND_TIMEOUT_MS` (10s) must outlive it plus the route's pre/post work
 * (queue depth, alarms, oldest-unrated anti-join) or the client aborts a
 * live round and the platform keeps rating as a zombie.
 *
 * WHY THIS EXISTS (#2280, 2026-09-04): one tenant burst to ~330 RPM. Events
 * of ONE account rate serially (shared `account_balances` row lock), so a
 * 500-row page owned by one account needs minutes, not 10s. Without a
 * server-side deadline every round timed out client-side, the platform
 * finished the page in the background and acked it, the next tick re-peeked
 * the same page, and `rating_drain` logged `ok:false` 20–29 of every 30
 * ticks while only the 80-row backstop sweep made progress. With the deadline
 * each round returns in ~5s with partial progress, the 45s drain budget fits
 * ~6 rounds, and the un-acked page is resumed rather than restarted.
 * (The old client bound was a transcribed 10_000; it is now derived, below.)
 *
 *   control-loops ROUND_TIMEOUT_MS = RATE_EVENTS_DRAIN_DEADLINE_MS
 *                                   + DRAIN_RESPONSE_HEADROOM_MS (5_000)
 *
 * Derived on the client, like the sweep's, so moving this constant moves the
 * round timeout with it. The deadline is an ABSOLUTE instant from route
 * entry: the depth read, the alarm evaluations, the peek and the page load
 * all spend it rather than add to it. What the headroom must cover is what
 * happens AFTER the deadline: up to `DEADLINE_MIN_ATTEMPTS` events when none
 * has rated yet (poisoned head-of-page), otherwise the one event in flight,
 * plus the reply. The full-table oldest-unrated read is skipped on the
 * stopped-early path so nothing unbounded sits in that window.
 *
 * KNOWN RESIDUAL (review S1'): the pre-work is bounded only by its own
 * timeouts — Tair depth+peek 3s each, up to two Lark sends 5s each — which
 * can in the worst case exceed the round timeout on their own, and the PG
 * reads carry no statement_timeout, the charge's FOR UPDATE no lock_timeout.
 * That is a pre-existing property of the route; this constant narrows the
 * zombie window, it does not close it. Tracked in #2283.
 */
export const RATE_EVENTS_DRAIN_DEADLINE_MS = 5_000;

/**
 * `SET LOCAL statement_timeout` inside the page engine's settle transaction
 * (#2684 L-B, `POST /internal/cron/rate-events/page`). One page is ONE settle
 * transaction for every account on it, so a single runaway statement would
 * hold L1 of up to 500 accounts; 4 s keeps the worst case (one settle started
 * just before the 5 s drain deadline) inside the client's 10 s
 * `ROUND_TIMEOUT_MS`. A timeout is an ordinary error to the engine: the page
 * is bisected and retried in smaller transactions, never counted failed
 * wholesale. Production estimate for a full 500-event page is ~1.2 s of
 * execution (#2684 §2.1), local ~0.1 s.
 */
export const RATE_EVENTS_PAGE_STATEMENT_TIMEOUT_MS = 4_000;

/**
 * `RATING_DRAIN_CONSUMERS` (K) is bounded twice since R1 of the billing-chain
 * 15W plan (router#2539, owner decision E3 2026-10-07): ONE knob, two caps
 * that depend on who drives the drain.
 *
 *   - RATING_DRAIN_CONSUMERS_TICK_CAP (4): the Cloudflare tick and its Node
 *     fallback ticker (`dispatchTickWith`). A value in (4, 16] is capped to 4
 *     there with a `configWarning`; derivation a–e below.
 *   - RATING_DRAIN_CONSUMERS_CEILING (16): the billing-loops driver (lease
 *     holder kind `loops`), which runs outside Cloudflare, so a and b do not
 *     apply; what bounds it is f below.
 *
 * Default 1; outside [1, 16] (or not an integer) falls back to 1 with a
 * `configWarning`, as before. K >= 2 runs K account lanes
 * (`POST /internal/cron/rate-events/lanes`) concurrently inside the one
 * `rating_drain` call. THE TICK CAP is the minimum of these bounds:
 *
 *   a. Cloudflare Workers, every plan: at most 6 connections per invocation
 *      may be waiting for response headers at once; a 7th fetch is QUEUED
 *      until one of them gets its headers, and a queued fetch still burns its
 *      ROUND_TIMEOUT_MS (developers.cloudflare.com/workers/platform/limits,
 *      "Simultaneous open connections", read 2026-09-27). A lane round holds
 *      its connection for the whole page, so K <= 6. The other handlers of
 *      the tick run SERIALLY before and after this one (`dispatchTickWith`,
 *      schedule.ts: `for (const spec of due) await runHandlerWithTimeout(...)`),
 *      so nothing else of the invocation competes for those 6 while the lanes
 *      run; 4 keeps 2 spare for the handler's own post-work and for a round
 *      whose response body is still streaming. => K <= 4. Exception (#2854):
 *      the credit-draw sweep lane (`CREDIT_DRAW_SWEEP_LANE_MS`, a `concurrent`
 *      handler) holds one more connection beside the lanes for its whole
 *      share, so with it on, K lanes + post-work + lane = K + 2 <= 6 — K = 4
 *      reaches the limit exactly, and a 7th fetch would queue on rating's time
 *      (one more reason for K <= 3 with the lane on, see c).
 *   b. Subrequests: 10 000 per invocation on the paid plan by default
 *      (configurable up to 10 M; free plan 50 — same page). K lanes x
 *      RATING_DRAIN_LANE_ROUNDS_CEILING (400) = 1 600 at K = 4, leaving the
 *      rest of the tick's handlers > 8 000. The ceiling is per lane over the
 *      tick: a lane that control-loops runs again after it stopped (PR #2707)
 *      counts on from the rounds it already took, so it stays K x 400.
 *      Not binding. Cron CPU (30 s
 *      default, 5 min max) and wall clock (15 min) are far above a 55 s
 *      handler that mostly awaits I/O. Not binding.
 *   c. Platform PostgreSQL pool (`PLATFORM_PG_POOL_MAX`, default = pg's 10,
 *      no connection timeout: a full pool QUEUES). A lane round uses one
 *      connection at a time (plan read, pricing reads, settle transaction,
 *      allocation transaction run in sequence), two at the brief overlap of a
 *      pool read and a transaction. Worst case every lane lands on the same
 *      host (Node fallback ticker, or the LB): 2K lane connections + the
 *      backstop sweep's 1 held advisory transaction + up to 4 of its own
 *      + >= 2 left for tenant and ops API traffic must fit the pool:
 *      2K + 5 + 2 <= max  =>  K <= 1 at 10, K <= 6 at 20. Hence the
 *      precondition: PLATFORM_PG_POOL_MAX = 20 before any K > 1
 *      (docs/runbooks/rating-rollout.md). Armed, the credit-draw catch-up
 *      (#2752) holds up to 4 more, one per account in flight. It runs before
 *      rating_drain in the same tick, so it overlaps the lanes only when its
 *      round was abandoned by control-loops (timeout) while the platform
 *      finishes the charges in flight; budgeting for that overlap:
 *      2K + 5 + 4 + 2 <= max  =>  K <= 4 at 20 — and at 10 not even K = 1
 *      fits, so PLATFORM_PG_POOL_MAX = 20 is also a precondition of
 *      CREDIT_DRAW_CATCHUP_ARMED (a full pool queues; it does not fail).
 *      The credit-draw sweep lane (#2854, `CREDIT_DRAW_SWEEP_LANE_MS`) runs
 *      BESIDE rating_drain every minute but :x7, not before it, and a lane
 *      round holds 2 connections for its whole share (the single-flight lock
 *      transaction, idle in transaction, and the per-charge transactions);
 *      with the catch-up's overlap kept:
 *      2K + 5 + 4 + 2 + 2 <= max  =>  K <= 3 at 20, K = 4 needs 22. Hence the
 *      precondition of turning the lane on: K <= 3 (RATING_DRAIN_CONSUMERS),
 *      or PLATFORM_PG_POOL_MAX >= 22 (docs/runbooks/rating-rollout.md).
 *   d. RDS: max_connections 1 620, ~45 backends in production (2026-09-26).
 *      Not binding. Its write IO is the NAMED bottleneck #2684 reports; K only
 *      decides whether it is reached.
 *   e. The 60 s scheduler bound holds per lane, independent of K: each lane
 *      checks DRAIN_BUDGET_MS (45 s) before a round and a round is bounded by
 *      ROUND_TIMEOUT_MS (10 s), so Promise.all of K lanes ends by 55 s
 *      (control-loops rating-drain.ts, unit-tested).
 *
 * THE LOOP CEILING (16):
 *
 *   f. The loop driver is a Node process on a platform host: no 6-connection
 *      or subrequest limit (a, b gone). What binds is the executing platform
 *      pool (c, with the lanes no longer beside the tenant API once they run
 *      in `platform-billing`, design §3.4: 2K + allocator 8 + credit draw 8
 *      + leases/brake 2 + 4 spare = 54 at K = 16, i.e. a 56–64 pool) and the
 *      RDS write rate (WAL per event, the named wall of #2539 §2.7). 16 is
 *      twice the K = 8 target that covers 220 000 events/minute at the
 *      measured single-lane rate (design §3.1). Pool sizing is an operator
 *      precondition of K > 4 under the loop (docs/runbooks/rating-rollout.md),
 *      not checked here.
 */
export const RATING_DRAIN_CONSUMERS_CEILING = 16;

/** K under the Cloudflare tick and its Node fallback ticker: bounds a–e above. */
export const RATING_DRAIN_CONSUMERS_TICK_CAP = 4;

// ─── POST /internal/cron/rate-events/lanes (#2684 L-C, design S4′) ─────────
//
// One lane call rates ONE page drawn from the gateway's pending set
// (`usage_rating_pending`, migration 0091) by ACCOUNT: candidate accounts in
// oldest-pending-first order, each account's oldest unrated events, up to
// RATE_EVENTS_LANE_PAGE_MAX. The claim IS the account row lock the page
// engine takes (`account_balances FOR UPDATE SKIP LOCKED`): an account another
// lane (or the sweep) holds is skipped and the page refilled from the next
// candidates. No Tair state; the doorbell is not read.

/** Events per lane page (the page engine's settle transaction). */
export const RATE_EVENTS_LANE_PAGE_MAX = 500;

/**
 * Candidate accounts per plan, oldest pending first. 512 x (index probe +
 * the account's rated walk) keeps the plan in milliseconds; an account past
 * this rank waits for the ones ahead of it to drain (§3.7 bound).
 */
export const RATE_EVENTS_LANE_CANDIDATE_ACCOUNTS_MAX = 512;

/**
 * Accounts the loose index scan visits per plan before handing the rest to
 * the next call (`scanCursor`). Bounds the plan when very many accounts have
 * pending rows; far above today's 55 active accounts.
 */
export const RATE_EVENTS_LANE_SCAN_ACCOUNTS_MAX = 4096;

/**
 * Lane sharding (#2704). With K lanes, every account belongs to ONE lane:
 * `mod(hashtext(account_id) + 2^31, K)` (stable across ticks and hosts for
 * a given K). A lane plans its own accounts first; an account of another
 * lane's shard ranks as if its oldest pending row were this much younger, so
 * a lane takes it only once its own shard has nothing older left, or once
 * that account has waited this long past the lane's own oldest (the owner is
 * behind: a hot account fills its pages, or it is slow). Without it every
 * lane planned the same oldest-first accounts and 40-60% of the lanes' work
 * was skipped (another lane held the account, or had already rated it). A
 * rank, not a claim: the claim is still the page engine's L1 lock. What a
 * lane never plans of a shard whose owner still runs this tick is that
 * owner's next RATE_EVENTS_LANE_RESERVE_ROWS (#2706; `stoppedLanes` lifts it,
 * #2704 review A1), and no lane plans another shard's account another lane is
 * on (`inFlight`). The same yield is how far a lane's own STICKY account
 * (`stickyAccounts`, #2706) ranks ahead of its age: the lane stays on the hot
 * account it is rating unless another of its accounts has waited a yield
 * longer (`apps/platform-api/src/rating/lane-claim.ts`); a sticky account
 * with at most 2 x RATE_EVENTS_LANE_RESERVE_ROWS unrated rows left ranks
 * first instead, so the lane finishes it (#2718).
 */
export const RATE_EVENTS_LANE_SHARD_YIELD_MS = 5_000;

/**
 * Rows of a running owner's shard no other lane plans (#2706): the first this
 * many in the order the owner itself plans them (its sticky accounts a yield
 * early, then oldest first, the accounts other lanes are on last). Two pages:
 * the one the owner has in flight and the next, which it plans when that one
 * settles. Another lane planning into them only met the owner's lock or rows
 * the owner had just rated: with the head alone reserved (#2704), a helper
 * whose own shard was empty took each shard's next-oldest accounts, exactly
 * the owner's page in flight or its next one — 20-40% of the lanes' work on
 * the local 5 000-event drills (K = 4) was skipped that way. What lies beyond
 * the owner's next two pages is still planned by other lanes: the owner will
 * not reach it before their page has settled.
 */
export const RATE_EVENTS_LANE_RESERVE_ROWS = 2 * RATE_EVENTS_LANE_PAGE_MAX;

/**
 * Accounts a lane request may name as its own `stickyAccounts`, and per lane
 * in `inFlight` (#2706), and `taken_left_accounts`. The heavy accounts of
 * one call's pages: at most three pages of 500, each with at most 5 accounts
 * of >= 100 events and the account the page ended on, i.e. 18.
 */
export const RATE_EVENTS_LANE_STICKY_ACCOUNTS_MAX = 64;

/**
 * Rows of an account of ANOTHER lane's shard a plan takes in its first pass
 * (#2704); the rest of that account comes after every other candidate. A lane
 * helping another shard sweeps its small accounts before it spends a page on
 * one of its hot ones (never a running owner's reserved rows, #2706).
 * Equal to the lanes' heavy threshold (LANE_HEAVY_ACCOUNT_EVENTS): more than
 * this is a hot account.
 */
export const RATE_EVENTS_LANE_FOREIGN_SLICE_MAX = 100;

/**
 * Pages per lane CALL: the first, plus at most two refills when more than
 * half of a page's events belonged to accounts another rater held (design
 * §3.6 step 4) — the lane moves on to other accounts instead of idling.
 */
export const RATE_EVENTS_LANE_PASSES_MAX = 3;

/**
 * Events a request may name in `avoidRequestIds` (batch 2 review R4): the
 * events that failed earlier in the same tick. A lane skips exactly those,
 * not their account — a poison event at the head of a hot account used to
 * put the whole account on every lane's avoid list and cap it at one page
 * (499 events) per tick.
 */
export const RATE_EVENTS_LANE_AVOID_REQUESTS_MAX = 256;

/** Accounts a request may name in `avoidAccounts` (a hint, never a rule). */
export const RATE_EVENTS_LANE_AVOID_ACCOUNTS_MAX = 128;

/**
 * Floors a request may carry, and a response may return. The platform keeps
 * its own per-host floors for every account it rated in full; the wire only
 * shares the heavy accounts' floors across lanes and hosts.
 */
export const RATE_EVENTS_LANE_FLOORS_MAX = 64;

/**
 * An account whose page share reaches this many events and was rated in full
 * gets a floor in the response: the next plans in the same tick read that
 * account's pending rows from `floor - RATE_EVENTS_LANE_FLOOR_SKEW_MS` on,
 * instead of re-walking its already-rated rows the gateway has not pruned
 * yet (C′: platform never deletes pending rows). Hint only.
 */
export const RATE_EVENTS_LANE_FLOOR_MIN_EVENTS = 20;

/**
 * Margin below a floor that is still read: a pending row stamped (trigger
 * `clock_timestamp()`) before the floor but committed after it — commit
 * order is not stamp order — is read by the next plan unless the gap exceeds
 * this. A longer gap only delays it: wire floors live for one tick, the
 * platform's own for at most LANE_FLOOR_CACHE_TTL_MS from when they were
 * first set (never renewed, batch 2 review R7), and the backstop sweep reads
 * the whole pending set. 2 s (was 1 s, R7): a metering transaction that
 * waits on a lock between its stamp and its commit can take seconds; the
 * absolute TTL catches a longer one within a minute. Every plan walks this
 * much of each floored account's rated-but-unpruned rows, so it is not free:
 * 5 s cost about 5% of lane throughput on the local drill (uniform, K = 4).
 */
export const RATE_EVENTS_LANE_FLOOR_SKEW_MS = 2_000;

/**
 * `SET LOCAL statement_timeout` around one lane plan (batch 2 review R-2). The
 * plan normally takes milliseconds; red team #10 measured 2.7–3.8 s with
 * 2 M rated rows left in the pending set. A plan that would take longer is
 * cancelled (SQLSTATE 57014) instead of eating the round's 10 s client
 * timeout; the call's first plan is then retried ONCE over half the accounts
 * (RATE_EVENTS_LANE_PLAN_RETRY_STATEMENT_TIMEOUT_MS), a refill's is not
 * (#2704 review A4).
 */
export const RATE_EVENTS_LANE_PLAN_STATEMENT_TIMEOUT_MS = 3_000;

/**
 * `statement_timeout` of the one retry a lane call makes when its FIRST plan
 * was cancelled by RATE_EVENTS_LANE_PLAN_STATEMENT_TIMEOUT_MS (#2704 review
 * A4). The retry scans half as many accounts (`scanAccountsMax / 2`; the rest
 * is the next call's, through `scanCursor`). What a plan costs grows with the
 * accounts it scans — the loose scan and, per account, the walk to its oldest
 * unrated row over the rated rows the gateway has not pruned yet (red team
 * #10) — so half the scan in 2 s fits any plan of up to ~4 s whose cost is
 * spread over its accounts. One whose cost is ONE account's long rated prefix
 * is not helped by a smaller scan: it times out again and the call answers
 * 503 (`lane plan timed out`), which control-loops counts as a failed round.
 *
 * Only when it plans fewer accounts: a window holding no more than half the
 * cap is the same plan again (production today: ONE window, 55 accounts of
 * 4 096), so that case answers 503 at once (review round 3, M2).
 *
 * The round's budget (round 3, M2). Client timeout ROUND_TIMEOUT_MS =
 * RATE_EVENTS_DRAIN_DEADLINE_MS (5 s) + 5 s = 10 s, from control-loops'
 * send. The route's drain deadline is route entry + 5 s, and before its
 * first plan the route reads the kill switch, probes the pending set and runs
 * the switch guard: g, ~1 s on a slow database. Every plan's statement_timeout
 * is min(its constant, deadline - now) and another plan (retry, empty-window
 * hop, refill) starts only with RATE_EVENTS_LANE_PLAN_MIN_MS left, so the
 * plans end by the deadline, the first by at most that minimum past it when
 * g alone used it up: max(5 s, g + 0.5 s). Then one settle past the deadline,
 * bounded by RATE_EVENTS_PAGE_STATEMENT_TIMEOUT_MS (4 s), and the reply:
 * ~9 s for g < 4.5 s. Without the clamp it was g + 3 s + 2 s of plans, then
 * a settle: 10 s at g = 1 s.
 */
export const RATE_EVENTS_LANE_PLAN_RETRY_STATEMENT_TIMEOUT_MS = 2_000;

/**
 * The least time before the drain deadline a lane call starts ANOTHER plan
 * with (#2704 review round 3, M2): the first plan's retry, a hop to the next
 * empty scan window, a refill. Every plan's statement_timeout is also
 * `min(its constant, deadline - now)` (the call's first plan at least this
 * much), so no plan outlives the drain deadline by more than this, and the
 * settle after the deadline keeps its headroom (see
 * RATE_EVENTS_LANE_PLAN_RETRY_STATEMENT_TIMEOUT_MS for the round's budget).
 * A plan takes milliseconds when healthy (local drill: 5-40 ms); one given
 * less than half a second would only be cancelled.
 */
export const RATE_EVENTS_LANE_PLAN_MIN_MS = 500;

/**
 * `statement_timeout` around the lane tick's oldest-unrated measurement (the
 * `postWorkOnly` call's read of the pending set, #2704 review item 4). It is
 * one statement over EVERY pending row with a rated probe per row, O(pending):
 * on a backlog, or with rated rows the gateway has not pruned yet (red team
 * #10 left 2 M), it is the slowest read the call makes. The call's client
 * timeout is ROUND_TIMEOUT_MS (10 s) and the call also reads the bell's depth,
 * the recent metering failures and the uplink status, and may post two alarm
 * cards; 5 s leaves those the other half. A measurement cancelled by it is
 * NOT a failed read: the route serves the memo extrapolated to now (exact or
 * pessimistic, the safe side for this alarm) and only without a usable memo
 * reports `unknown`, as a failed read always did.
 */
export const RATE_EVENTS_LANE_MEASURE_STATEMENT_TIMEOUT_MS = 5_000;

export interface RateEventsLaneFloor {
  readonly accountId: string;
  /** `usage_rating_pending.enqueued_at::text` (microsecond precision). */
  readonly enqueuedAt: string;
}

export interface RateEventsLaneRequest {
  /** 0-based lane index (logging / response echo). */
  readonly lane: number;
  /** K of this tick. */
  readonly lanes: number;
  /** Accounts not to plan this call: held by another lane. */
  readonly avoidAccounts?: readonly string[];
  /** Events not to plan this call: they failed earlier in this tick (R4). */
  readonly avoidRequestIds?: readonly string[];
  readonly floors?: readonly RateEventsLaneFloor[];
  /** Continue the loose account scan after this account id. */
  readonly scanAfter?: string;
  /**
   * Lanes of THIS tick that have stopped (any `stoppedBy`), #2704 review A1.
   * Each index in [0, lanes); duplicates and this lane's own index are
   * ignored; absent or empty = every lane is running. A lane leaves another
   * shard's reserved rows (RATE_EVENTS_LANE_RESERVE_ROWS, #2706; the shard's
   * HEAD before it) to its owner only while that owner runs: once the owner
   * has stopped, they are planned like any other account of that shard, so a
   * lane that stopped early (errors, idle rounds, a short page) does not
   * leave its oldest accounts pending until the next tick. A stopped lane is
   * also no account's designated helper any more. The accounts of a
   * stopped lane's shard also count as this lane's own in
   * `skipped_locked_own`.
   */
  readonly stoppedLanes?: readonly number[];
  /**
   * The heavy accounts of THIS lane's previous page (#2706), i.e. what the
   * lane is rating: control-loops sends the previous response's
   * `heavyAccounts`. With `lanes` > 1 the lane plans them first among its
   * own accounts (a yield ahead of their age) instead of jumping to whichever
   * hot account of its shard is oldest, and goes on with one it is helping
   * with. Ignored with `lanes` = 1. Absent = none (an older control-loops:
   * the lanes rank by age alone).
   */
  readonly stickyAccounts?: readonly string[];
  /**
   * The other lanes of THIS tick that are running and the accounts each is
   * sticky on (#2706): what control-loops sent as their `stickyAccounts` for
   * the call they have in flight, then the heavy accounts of its answer
   * until its next round (never after a failed round or its stop). With
   * `lanes` > 1 an account named here is that lane's to plan: other lanes
   * leave it out, its owner too unless the owner is sticky on it as well
   * (the owner keeps it). Unlike the one-round-late avoid hint, which once
   * stopped every lane "drained" with a hot account pending (#2704), a lane
   * is named only while it runs and with what its own plan puts first. The
   * owners' reserved rows (RATE_EVENTS_LANE_RESERVE_ROWS) are predicted with
   * each owner's sticky accounts. With this field present (even empty) the
   * helpers of an account take turns: its designated helper, then the next
   * lanes in a fixed rotation, each only while those before it have stopped
   * or are busy (sticky on at least RATE_EVENTS_LANE_RESERVE_ROWS unrated
   * rows) — so two free lanes never plan the same accounts, not even in the
   * tick's first round. Each lane at most once, never this lane; at most
   * RATE_EVENTS_LANE_STICKY_ACCOUNTS_MAX accounts each; a lane on nothing
   * need not be named. Absent = an older control-loops: nothing is known,
   * every helper may plan any account.
   */
  readonly inFlight?: ReadonlyArray<{ readonly lane: number; readonly accounts: readonly string[] }>;
  /**
   * Once per tick, BESIDE the lanes (batch 2 review R6): the legacy route's
   * alarm reads (metering failures, the oldest-unrated age and its card, the
   * bell's depth for the record) and nothing else — no plan, no rating. A lane
   * round never carries them: on a slow database they used to push lane 0's
   * first round past its 10 s timeout and fail the tick.
   */
  readonly postWorkOnly?: boolean;
}

export interface RateEventsLaneCronResponse {
  readonly ok: true;
  readonly engine: 'lanes';
  readonly lane: number;
  /** The page bound this route used (RATE_EVENTS_LANE_PAGE_MAX). */
  readonly pageMax: number;
  /** Events handed to the engine across this call's passes. */
  readonly claimed: number;
  readonly rated: number;
  readonly charged: number;
  readonly insufficient: number;
  readonly unpriced: number;
  readonly skippedAlreadyRated: number;
  readonly failed: number;
  readonly skipped_locked: number;
  /**
   * The part of `skipped_locked` on accounts this lane ANSWERS FOR (#2704
   * review A3): its own shard, and the shards of `stoppedLanes`; always 0
   * with `lanes` = 1. Counted only once a later plan of the same call came
   * back without those accounts (the call's refill, which avoids them), so
   * when `skipped_locked_own` = `skipped_locked` and `claimed -
   * skipped_locked_own` < `pageMax`, the call's LAST plan was short: the lane
   * found everything it may take, and what it left is held by a rater no lane
   * of the tick will out-wait (an outside transaction, the sweep). control-loops
   * reads two such rounds in a row as a short page, so a hot account held from
   * outside no longer keeps every lane polling to the end of the budget.
   * Absent on an older platform (read as 0).
   */
  readonly skipped_locked_own: number;
  /** As on {@link RateEventsCronResponse.allocation_deferred}. Absent when 0. */
  readonly allocation_deferred?: number;
  /** As on {@link RateEventsCronResponse.ledger_write_frozen}. Absent when 0. */
  readonly ledger_write_frozen?: number;
  /** Accounts that had at least one event rated by this call. */
  readonly accounts: number;
  /** Planned accounts another rater held (skipped, left pending). */
  readonly lockedOut: number;
  readonly passes: number;
  readonly stopped_early?: true;
  readonly lockedOutAccounts: readonly string[];
  /** Accounts with an event that was planned, not held elsewhere, and not rated (logging). */
  readonly failedAccounts: readonly string[];
  /** Those events (capped at 64): what the next rounds of the tick skip. */
  readonly failedRequestIds: readonly string[];
  /**
   * Accounts with >= 100 events on this call's pages (likely still hot), and
   * the account a full page ended on (#2706: the lane is inside it).
   */
  readonly heavyAccounts: readonly string[];
  readonly floors: readonly RateEventsLaneFloor[];
  /** Set when the loose scan stopped at RATE_EVENTS_LANE_SCAN_ACCOUNTS_MAX. */
  readonly scanCursor: string | null;
  /**
   * #2706: accounts the call's LAST plan left out only because they were not
   * this lane's helper turn (`inFlight`): a helper before it runs and is not
   * known busy. control-loops does not let a lane stop "drained" on such a
   * plan before every lane of the tick has answered once, then plans again.
   * Absent when 0, and from an older platform.
   */
  readonly turn_left?: number;
  /**
   * #2706: on a short last plan, how many of this lane's OWN accounts it left
   * out because another running lane was on them (`inFlight`). That lane may
   * have moved on by the time this answer is read; control-loops then plans
   * once more rather than stop "drained" with those accounts pending (both
   * lanes stopped with one of them left, 1 run in 3-6 on skew K = 2 20 000
   * events). Since #2974 it also counts, with K >= 3, accounts of a STOPPED
   * owner's shard it left out because another helper was on them: two
   * helpers both on such an account each left it to the other and both
   * stopped "drained", and nobody rated it before the next tick. It takes a
   * lane on the account that is neither its owner nor this lane, so with
   * K = 2 the count is what it was. Absent when 0, and from an older platform.
   */
  readonly taken_left?: number;
  /**
   * #2706: those accounts (at most RATE_EVENTS_LANE_STICKY_ACCOUNTS_MAX),
   * with `taken_left`. A lane that stops "drained" on such a plan runs again
   * when the lane on one of them drops it: that lane may leave it unrated
   * (its own hot account ranks first), and with the owner stopped it waited
   * behind that hot account for the rest of the tick.
   */
  readonly taken_left_accounts?: readonly string[];
  readonly queue_depth: number | null;
  readonly oldest_unrated_age_ms: number | null;
  readonly usage_log_failed_recent: number | null;
}

/**
 * Request body for `POST /internal/usage-events/cleanup` (task D20; protocol
 * reordered by #2375 / #2354).
 *
 * Two shapes, and the receiving region does not change what they mean:
 *
 * - **No body / no `requestIds`: SELECT-ONLY.** The Tokyo gateway answers with
 *   `reapableRequestIds` — the page of request_ids whose rated sibling is past
 *   retention — and deletes NOTHING. The CN gateway refuses this shape (it has
 *   no rated table to consult) and also deletes nothing.
 * - **`requestIds`: ALLOWLIST DELETE.** Either region deletes exactly the named
 *   rows, bounded by `RATING_EVENTS_CLEANUP_MAX_ROWS`, and never applies the
 *   rated-sibling predicate itself.
 *
 * The rated-sibling predicate therefore selects rows but never deletes them
 * inside the same call: the only writer that deletes on Tokyo is the
 * control-loops handler, and it does so AFTER the CN copy of the same list is
 * gone. `usage_events` is an uplink table (CN publisher → Tokyo subscriber,
 * `publish = 'insert, update'`): a CN DELETE never replicates, so a Tokyo-first
 * delete used to leave the CN originals behind for good — and a CN-side
 * rebuild with `--backfill-uplink` would resurrect them on Tokyo. Tokyo-last
 * closes both holes.
 */
export interface UsageEventsCleanupRequest {
  readonly requestIds?: readonly string[];
  /**
   * Select shape only (#2759 P3 second step): the reaper yields the days the
   * whole-day truncate owns. Tokyo bounds its page to rows completed before
   * `GREATEST(Shanghai midnight(today − usageEventsReaperYieldLineDays), T)`
   * and answers that bound as `completedBefore`. Sent only while the yield
   * is effective; absent = the pre-yield select, statement for statement.
   */
  readonly yieldToDayTruncate?: true;
  /**
   * Allowlist shape only: the `completedBefore` the select answered. Both
   * deletes add `completed_at < completedBefore`, which every listed id
   * satisfies (the select chose them under it), so it removes nothing from
   * the list; it lets the planner prune the partitions the day truncate
   * holds locks on. Absent = the pre-yield DELETE, statement for statement.
   */
  readonly completedBefore?: string;
}

/**
 * Rows deleted from the gateway-owned metering outbox (task D20).
 *
 * `skippedUnrated` is the count the predicate deliberately refused to touch.
 * It is the number to watch: a persistently non-zero value means the rating
 * pipeline has a permanent tail, not that the reaper is broken.
 */
export interface UsageEventsCleanupResponse {
  /** Rows this call deleted. Always 0 on the select-only shape. */
  readonly deletedRows: number;
  /**
   * Select-only shape on Tokyo: events completed before the retention window
   * that have no rated fact, counted from the rating pending set
   * (`usage_rating_pending`) since #2710 — O(size of the pending set), never
   * a scan of `usage_events`. Exact when the pending set is complete, i.e.
   * when the rating-rollout runbook's `missing` and `orphans` reconciliation
   * queries are both 0 (platform runs `missing` itself only while
   * `RATING_UNRATED_SOURCE=pending_table`); a lower bound before the pending
   * set's backfill has finished.
   *
   * `null` = NOT COUNTED this call: the count runs under its own budget
   * after the page and, when it fails (timeout, lock wait, any error), the
   * select still returns the page — which the caller deletes as usual — and
   * `errors` carries the reason. It is never a reason to skip a delete.
   *
   * Always 0 on the allowlist shape; on CN's refused select it is the count
   * of events past the window (CN has no rated table).
   */
  readonly skippedUnrated: number | null;
  readonly errors: readonly string[];
  /**
   * Select-only shape on Tokyo: the rated-past-retention request_ids this
   * page would reap, at most `RATING_EVENTS_CLEANUP_MAX_ROWS`. Nothing was
   * deleted for them yet. Absent on the allowlist path and on CN.
   * Un-uplinked rows never appear (they do not exist on Tokyo).
   */
  readonly reapableRequestIds?: readonly string[];
  /**
   * Select-only shape: the page bound this call used (#2681) — the contract
   * default unless `RATING_EVENTS_CLEANUP_MAX_ROWS` is set and valid. The
   * allowlist shape never reads the knob (it caps at the ceiling), so it
   * carries neither this nor `configWarnings`.
   */
  readonly limits?: { readonly maxRows: number };
  /** Knobs this call IGNORED (invalid or above ceiling). Absent when none. */
  readonly configWarnings?: readonly string[];
  /**
   * Select shape with `yieldToDayTruncate` only: the bound the page used
   * (ISO). The caller passes it back on both allowlist deletes.
   */
  readonly completedBefore?: string;
}

/**
 * Retention window for `usage_events`, measured on the RATED sibling's
 * `rated_at` — never on the event itself. Default for the
 * `RATING_EVENTS_RETENTION_DAYS` process-env knob (#2560); an invalid
 * value fails the process at boot rather than silently falling back.
 */
export const RATING_EVENTS_RETENTION_DAYS = 14;

/**
 * Platform 0129's late-mark rule (router#3382): a verdict whose event's Shanghai day is before the Shanghai
 * day it is inserted on (by `clock_timestamp()`, not `rated_at`, which is its transaction's start) gets one
 * `usage_rated_late_marks` row in its own transaction. This is the trigger's `WHEN` text, verbatim; a test
 * pins the migration to it. A cold rated revision records how many marks its day had before its first page
 * was read; the platform reads a day cold only while the day holds no more.
 */
export const RATED_LATE_MARK_WHEN_SQL =
  "(NEW.event_at AT TIME ZONE 'Asia/Shanghai')::date < (pg_catalog.clock_timestamp() AT TIME ZONE 'Asia/Shanghai')::date";

const SHANGHAI_OFFSET_MS = 8 * 3_600_000;

/** {@link RATED_LATE_MARK_WHEN_SQL} in JS (Shanghai has no DST): is a verdict inserted at `insertAtMs` marked. */
export function ratedLateMarked(eventAtMs: number, insertAtMs: number): boolean {
  return Math.floor((eventAtMs + SHANGHAI_OFFSET_MS) / 86_400_000) < Math.floor((insertAtMs + SHANGHAI_OFFSET_MS) / 86_400_000);
}

/**
 * The earliest a rated revision's mark baseline may be read: this long after its day's end. A verdict of
 * the day that is not marked was inserted on the day itself; to be missed by a revision whose baseline was
 * read later it must commit after that read, i.e. stay open this long, which
 * {@link RATED_WRITE_TRANSACTION_TIMEOUT_MS} forbids.
 */
export const RATED_LATE_MARK_BASELINE_MIN_AFTER_DAY_MS = 3_600_000;

/**
 * `transaction_timeout` of every transaction that writes `usage_rated_events` (router#3382): its first
 * statement sets it, transaction-local, so the timer starts there (PG 18 starts it at the SET, not at BEGIN).
 * A transaction still open after it is ended by the server (SQLSTATE 25P04, the connection is closed) and the
 * page is rated again on a later pass; rating is idempotent per request id. Production rating transactions
 * took at most 65 ms (2026-10-27 sample).
 */
export const RATED_WRITE_TRANSACTION_TIMEOUT_MS = 60_000;

/**
 * Upper bound of "a verdict is visible this long after its `rated_at`": the write transaction's timeout plus
 * 60 s for the round trip between BEGIN (`rated_at` is the transaction start) and the timeout statement, and
 * for clock reads. Every `rated_at` watermark of a derived table trails its tick by at least this much
 * ({@link RATED_WATERMARK_MARGIN_MS}), so a row with `rated_at` at or before the watermark was committed before
 * the tick's scan.
 */
export const RATED_COMMIT_DELAY_BOUND_MS = RATED_WRITE_TRANSACTION_TIMEOUT_MS + 60_000;

/**
 * How far the hour fold's and the day rollup's `rated_at` watermark trails the tick (`now - margin`). It must be
 * at least {@link RATED_COMMIT_DELAY_BOUND_MS} (a test pins it); the rest absorbs clock skew between the app
 * host and the database.
 */
export const RATED_WATERMARK_MARGIN_MS = 15 * 60_000;

/** The statement every rated write transaction starts with ({@link RATED_WRITE_TRANSACTION_TIMEOUT_MS}). */
export function ratedWriteTransactionTimeoutSql(timeoutMs: number = RATED_WRITE_TRANSACTION_TIMEOUT_MS): string {
  if (!Number.isInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 600_000) throw new Error(`invalid rated write transaction timeout ${timeoutMs}`);
  return `SET LOCAL transaction_timeout = '${timeoutMs}ms'`;
}

/**
 * Page bound for one cleanup pass. An unbounded DELETE on a table taking
 * ~4000 rows/h/host holds locks for an unbounded time on the database serving
 * auth. Hourly x 20k is ~5x the write rate, so the reaper catches up from any
 * backlog while never running long. Default for the gateway's
 * `RATING_EVENTS_CLEANUP_MAX_ROWS` knob (#2681, ceiling 40 000 in
 * `reaper-limits.mjs`); the knob sizes the select page only.
 */
export const RATING_EVENTS_CLEANUP_MAX_ROWS = 20_000;

/**
 * Ids per CN allowlist DELETE call in `rating_events_cleanup`. Default for
 * the control-loops `RATING_EVENTS_CLEANUP_CN_CHUNK_ROWS` knob (ceiling
 * 40 000 in `reaper-limits.mjs`). Equal to the default page, so with both
 * knobs unset the CN leg is one call over the whole page, exactly as before
 * the knob existed. A smaller value splits the CN delete into several calls,
 * each under its own fetch bound (production 2026-10-01: one 20 000-id CN
 * DELETE on the 2-core Shanghai instance overran the 10 s bound on 3 of 23
 * hourly passes).
 */
export const RATING_EVENTS_CLEANUP_CN_CHUNK_ROWS = RATING_EVENTS_CLEANUP_MAX_ROWS;

// ─── POST /internal/pub-serviceability/recompute (#1405, ADR-009 §8) ────────

/**
 * How one recompute pass ended. Three outcomes, deliberately distinguished —
 * an operator reading `ok: true` alone cannot tell a pass that published from
 * a pass that never ran.
 *
 * - `completed` — the pass resolved every scope and published its diff.
 * - `skipped` — a previous pass still held the advisory lock. Not an error and
 *   not a failure to alert on: the running pass is doing the work.
 * - `pass_incomplete` — the pass hit its internal deadline and rolled back, so
 *   NOTHING was published. Loud on the first occurrence by design: staleness
 *   would otherwise be the only symptom, and it takes 60 minutes to show.
 */
export type PubServiceabilityRecomputeStatus = 'completed' | 'skipped' | 'pass_incomplete';

/**
 * Internal wall-clock budget for one gateway recompute pass (#1756).
 *
 * OWNED BY CONTRACTS BECAUSE IT IS A TWO-PARTY BOUND, not a gateway detail.
 * The gateway runs its whole single-transaction pass under this deadline and
 * only then answers `completed` or `pass_incomplete`; the control-loops client
 * must outlive it to hear that answer (`RECOMPUTE_FETCH_TIMEOUT_MS` = this
 * + response headroom, #1754), and the ops-api manual re-run must too. With
 * the number living in gateway source, all three copies could only be kept in
 * step by a comment — and #1754 is what that costs when they drift. Here the
 * client bound is asserted against this constant in test, so raising the
 * gateway deadline past a client goes red instead of shipping.
 *
 * Deliberately shorter than the 25s cron tick kill: a pass that cannot finish
 * must ROLL BACK and say so on its first occurrence, rather than being killed
 * by the tick and leaving staleness — which takes 60 minutes to become visible
 * on the reader side — as the only symptom.
 */
export const PUB_SERVICEABILITY_PASS_DEADLINE_MS = 20_000;

/**
 * Result of one gateway serviceability recompute pass (#1405).
 *
 * `changed` counts published row mutations, which is what gates the `version`
 * bump: a pass that changed nothing still touches `refreshed_at`, so a healthy
 * steady state reports `changed: 0` with `version` held constant. Reading a
 * rising `version` as "healthy" is therefore wrong — `refreshed_at` is the
 * liveness signal, `version` is the change signal.
 */
export interface PubServiceabilityRecomputeResponse {
  readonly ok: boolean;
  readonly status: PubServiceabilityRecomputeStatus;
  /** Scopes resolved this pass; global is not counted among them. */
  readonly scopes: number;
  readonly changed: number;
  /** Absent until the first successful pass has written the version row. */
  readonly version: number | null;
}

// ─── POST /internal/rating/replay-diff (#1652, feature #1649 S3) ────────────

/** Which usage_rated_events column bounds a replay window. */
export type ReplayTimeField = 'rated_at' | 'event_at';

/**
 * The gate verdict, SEPARATE from `ok`: `ok` says the run executed, the
 * verdict says what it found. A deploy sequence promotes only on `clean`
 * across every page.
 */
export type ReplayVerdict = 'clean' | 'diverged';

export type ReplaySkipReason =
  /** The stored row's columns violate the contract; nothing comparable. */
  | 'row_unreadable'
  /** Source usage_events row is gone (cleanup), so the kernel inputs it
   *  alone carries (usage_details_json + the authoritative token counts)
   *  are lost. */
  | 'usage_events_missing';

export interface ReplayFieldDiff {
  readonly request_id: string;
  readonly profile: string;
  readonly event_at: string;
  readonly rating_outcome: string;
  /** Report-only context (#1652): never part of the verdict. */
  readonly rate_source: string;
  readonly field: string;
  readonly stored: string;
  readonly recomputed: string;
}

export interface ReplayGroupSummary {
  readonly profile: string;
  readonly rating_outcome: string;
  readonly total: number;
  readonly compared: number;
  /** Rows (not fields) with at least one diff. */
  readonly diff_count: number;
  readonly skip_count: number;
}

export interface ReplayDiffReport {
  readonly verdict: ReplayVerdict;
  readonly window: {
    readonly field: ReplayTimeField;
    readonly from: string;
    readonly to: string;
  };
  readonly page: {
    readonly limit: number;
    readonly offset: number;
    readonly returned: number;
    /**
     * Cursor walk only (the request carried `cursor`): what to send as `cursor` for the next page, or
     * `null` when the window is fully walked. Absent on an offset page, whose report is unchanged.
     */
    readonly next_cursor?: string | null;
  };
  /**
   * All rated rows inside the window, page-independent (coverage math). On a cursor walk: counted when the
   * walk began and repeated on every page; the walk is complete when the summed `compared + skipped` of
   * its pages equals it (a verdict rated into the window after the walk began can make the sum differ).
   */
  readonly window_total: number;
  readonly compared: number;
  /** Rows whose recomputation actually ran the pricing kernel — the strict
   *  subset of `compared` that exercises the money math (outcome-only rows
   *  such as unpriced / sentinel-quarantined are compared without it). */
  readonly kernel_compared: number;
  readonly diff_row_count: number;
  readonly diff_field_count: number;
  readonly skipped: number;
  readonly skip_reasons: Readonly<Record<string, number>>;
  readonly groups: readonly ReplayGroupSummary[];
  readonly diffs: readonly ReplayFieldDiff[];
  readonly diffs_truncated: boolean;
}

/**
 * Rows per replay page. Deliberately EQUAL to RATE_EVENTS_MAX_LIMIT: one
 * page of the gate reads what one page of the engine rated, so the two
 * tools' paging arithmetic stays comparable during an incident.
 */
export const REPLAY_DIFF_MAX_LIMIT = 500;
export const REPLAY_DIFF_DEFAULT_LIMIT = 500;
/**
 * Rows per page of a cursor walk. Larger than an offset page on purpose: a cold day is walked in storage
 * order and each page re-reads the row group it starts in, so a bigger page reads each group fewer times.
 */
export const REPLAY_DIFF_CURSOR_MAX_LIMIT = 5000;
/** Detail rows carried in one report; the counters stay exact beyond it. */
export const REPLAY_DIFF_MAX_DETAILS = 200;
/** Widest admissible window; a longer range must be paged by window. */
export const REPLAY_DIFF_MAX_WINDOW_DAYS = 31;
